import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Download, Lock, Plus } from 'lucide-react';

import { estimateWorkoutMinutes } from '../../utils/workoutStats';
import { countSets } from './exerciseUtils';
import '../../styles/pages/treinos.css';
import { PreviewArt } from '../app/PreviewArt';

const list = (t, key, options) => t(`pages.Workouts.list.${key}`, options);

/** "8 exercícios · 24 séries · ~45 min" */
export const WorkoutMeta = ({ exercises }) => {
  const { t } = useTranslation();
  return (
    <>
      {list(t, 'exerciseCount', { count: exercises.length })} · {list(t, 'setCount', { count: countSets(exercises) })} ·{' '}
      {list(t, 'minutes', { count: estimateWorkoutMinutes(exercises) })}
    </>
  );
};

const Row = ({ row, isCurrent, isDesktop, onOpen }) => {
  const { t } = useTranslation();
  const isLocked = row.isLocked;
  return (
    <Link
      className={`mt-tr-row${isLocked ? ' mt-tr-row--locked' : ''}`}
      to={`/workouts?w=${row.id}`}
      // Desktop changes the pane beside the list; on phones the workout is a page of its own, so back closes it
      replace={isDesktop}
      state={{ fromList: true }}
      aria-current={isCurrent ? 'true' : undefined}
      onClick={(event) => onOpen?.(event, row)}
    >
      <span className="mt-tr-letter" aria-hidden="true">
        {row.name}
      </span>
      <span className="mt-tr-row__text">
        <span className="mt-tr-row__title">
          {list(t, 'workoutName', { name: row.name })}
          {row.isNext && !isLocked && <span className="mt-tr-pill">{list(t, 'today')}</span>}
          {isLocked && (
            <span className="mt-tr-pill mt-tr-pill--kept">
              <Lock aria-hidden="true" />
              {list(t, 'lockedTag')}
            </span>
          )}
        </span>
        {row.description && <span className="mt-tr-row__desc">{row.description}</span>}
        <span className="mt-tr-row__meta">{isLocked ? list(t, 'lockedMeta') : <WorkoutMeta exercises={row.exercises} />}</span>
      </span>
      <ChevronRight className="mt-tr-row__chevron" aria-hidden="true" />
    </Link>
  );
};

/**
 * Meus Treinos as an index: one row per workout (letter, description, what's in it), today's marked. The left pane on
 * desktop, beside the open workout or the editor; the whole page on phones. `onOpen` lets the editor ask before
 * leaving unsaved changes.
 */
export const WorkoutsListPane = ({
  rows,
  count,
  limit,
  isPro,
  isLoading,
  isDesktop,
  currentId,
  isExportingAll,
  unlockLabel,
  onNew,
  onExportAll,
  onUnlock,
  onOpen,
}) => {
  const { t } = useTranslation();
  const hasWorkouts = rows.length > 0;
  const keptCount = rows.filter((row) => row.isLocked).length;
  const roomLeft = Math.max(limit - rows.length, 0);

  const newButton = (
    <button type="button" className={`mt-tr-btn mt-tr-btn--go${isDesktop ? ' mt-tr-btn--sm' : ''}`} onClick={() => onNew()}>
      <Plus aria-hidden="true" />
      {list(t, 'new')}
    </button>
  );

  return (
    <section className="mt-tr-pane mt-tr-list" aria-labelledby="mt-tr-list-title">
      <div className="mt-tr-list__head">
        <div>
          <h1 id="mt-tr-list-title" className="mt-tr-list__title">
            {list(t, 'title')}
          </h1>
          {!isLoading && <p className="mt-tr-list__count">{list(t, 'count', { n: count, count: limit })}</p>}
        </div>
        {isDesktop && newButton}
      </div>
      {!isDesktop && hasWorkouts && (
        <div className="mt-tr-list__acts">
          {newButton}
          <button type="button" className="mt-tr-icon-btn" onClick={onExportAll} disabled={isExportingAll} aria-label={list(t, 'exportAll')}>
            <Download aria-hidden="true" />
          </button>
        </div>
      )}

      <div className="mt-tr-list__scroll">
        {/* Workouts kept from an ended Pro subscription: what they are and the way back to them */}
        {!isLoading && keptCount > 0 && (
          <section className="mt-tr-kept" aria-labelledby="mt-tr-kept-title">
            <PreviewArt kind="letters" badge="lock" size="sm" />
            <div className="mt-tr-kept__text">
              <h2 id="mt-tr-kept-title" className="mt-tr-kept__title">
                {list(t, 'keptTitle', { count: keptCount })}
              </h2>
              <p>{list(t, 'keptBody')}</p>
              <button type="button" className="mt-tr-btn mt-tr-btn--go mt-tr-btn--sm" onClick={onUnlock}>
                {unlockLabel}
              </button>
            </div>
          </section>
        )}

        {isLoading ? (
          <ul className="mt-tr-rows" aria-busy="true">
            {[0, 1, 2].map((index) => (
              <li key={index} className="mt-tr-row mt-tr-row--skeleton" />
            ))}
          </ul>
        ) : hasWorkouts ? (
          <ul className="mt-tr-rows">
            {rows.map((row) => (
              <li key={row.id}>
                <Row row={row} isCurrent={row.id === currentId} isDesktop={isDesktop} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-tr-empty">
            <PreviewArt kind="letters" />
            <h2 className="mt-tr-empty__title">{list(t, 'emptyTitle')}</h2>
            <p className="mt-tr-empty__text">{list(t, 'emptyBody')}</p>
            {/* Desktop has the ways to build it in the pane beside */}
            {!isDesktop && (
              <button type="button" className="mt-tr-btn mt-tr-btn--go" onClick={() => onNew()}>
                {list(t, 'emptyCta')}
              </button>
            )}
          </div>
        )}
      </div>

      {isDesktop && hasWorkouts && !isLoading && (
        <div className="mt-tr-list__foot">
          <button type="button" className="mt-tr-text-btn mt-tr-text-btn--ink" onClick={onExportAll} disabled={isExportingAll}>
            <Download aria-hidden="true" />
            {list(t, 'exportAll')}
          </button>
          <span>{isPro ? (roomLeft ? list(t, 'roomLeft', { count: roomLeft }) : list(t, 'roomFull')) : list(t, 'freePlan')}</span>
        </div>
      )}
    </section>
  );
};
